import React, { useState, useEffect } from 'react';

interface MatchResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (matchId: string, matchData: any) => void;
  match: any;
  updating: boolean;
  tournamentId?: number;
  onFetchPOM?: (espnScorecardUrl: string) => Promise<{ man_of_match?: string; message?: string } | void>;
}

const MatchResultModal: React.FC<MatchResultModalProps> = ({
  isOpen,
  onClose,
  onSave,
  match,
  updating,
  tournamentId,
  onFetchPOM
}) => {
  const [pomUrl, setPomUrl] = useState('');
  const [fetchingPOM, setFetchingPOM] = useState(false);
  const [pomError, setPomError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    winner: '',
    total_runs: '',
    man_of_match_player_name: '',
    status: 'completed'
  });

  useEffect(() => {
    if (match) {
      // Auto-populate with fetched data if available
      const fetchedData = match.fetchedData;
      console.log('🔍 Modal useEffect - match:', match);
      console.log('🔍 Modal useEffect - fetchedData:', fetchedData);
      console.log('🔍 Modal useEffect - winner from fetchedData:', fetchedData?.winner);
      console.log('🔍 Modal useEffect - winner from match:', match.winner);
      
      const newFormData = {
        winner: fetchedData?.winner || match.winner || '',
        total_runs: fetchedData?.totalRuns ? fetchedData.totalRuns.toString() : (match.total_runs ? match.total_runs.toString() : ''),
        man_of_match_player_name: match.man_of_match || '',
        status: fetchedData?.status || match.status || 'completed'
      };
      
      console.log('🔍 Modal useEffect - newFormData:', newFormData);
      console.log('🔍 Modal useEffect - match keys:', Object.keys(match));
      console.log('🔍 Modal useEffect - full match object:', match);
      
      // Try to extract team names from match_name if individual fields don't exist
      let team1Name = match.team1_name || match.team1;
      let team2Name = match.team2_name || match.team2;
      
      if (!team1Name || !team2Name) {
        // Extract from match_name like "San Francisco Unicorns vs Washington Freedom, 1st Match"
        const matchName = match.match_name || match.name || '';
        const vsIndex = matchName.indexOf(' vs ');
        if (vsIndex !== -1) {
          team1Name = matchName.substring(0, vsIndex).trim();
          team2Name = matchName.substring(vsIndex + 4).split(',')[0].trim();
        }
      }
      
      console.log('🔍 Modal useEffect - extracted team1:', team1Name);
      console.log('🔍 Modal useEffect - extracted team2:', team2Name);
      setFormData(newFormData);
    }
  }, [match]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!match) return;

    const matchData = {
      winner: formData.winner || null,
      total_runs: formData.total_runs ? parseInt(formData.total_runs) : null,
      man_of_match_player_name: formData.man_of_match_player_name || null,
      status: formData.status
    };

    onSave(match.match_id, matchData);
  };

  if (!isOpen || !match) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Set Match Result</h2>
              <p className="text-blue-100 text-sm">{match.team1} vs {match.team2}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-100 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Match Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Match Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="scheduled">Scheduled</option>
                <option value="live">Live</option>
                <option value="completed">Completed</option>
                <option value="abandoned">Abandoned</option>
              </select>
            </div>

            {/* Winner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Winner
              </label>
              <select
                value={formData.winner}
                onChange={(e) => setFormData({ ...formData, winner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={formData.status !== 'completed'}
              >
                <option value="">Select Winner</option>
                {(() => {
                  // Extract team names from match_name if individual fields don't exist
                  let team1Name = match.team1_name || match.team1;
                  let team2Name = match.team2_name || match.team2;
                  
                  if (!team1Name || !team2Name) {
                    const matchName = match.match_name || match.name || '';
                    const vsIndex = matchName.indexOf(' vs ');
                    if (vsIndex !== -1) {
                      team1Name = matchName.substring(0, vsIndex).trim();
                      team2Name = matchName.substring(vsIndex + 4).split(',')[0].trim();
                    }
                  }
                  
                  return (
                    <>
                      <option value={team1Name}>{team1Name}</option>
                      <option value={team2Name}>{team2Name}</option>
                    </>
                  );
                })()}
                <option value="draw">Draw</option>
                <option value="tie">Tie</option>
              </select>
              {formData.status !== 'completed' && (
                <p className="text-xs text-gray-500 mt-1">Winner can only be set for completed matches</p>
              )}
            </div>

            {/* Total Runs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Runs
              </label>
              <input
                type="number"
                value={formData.total_runs}
                onChange={(e) => setFormData({ ...formData, total_runs: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter total runs"
                min="0"
                disabled={formData.status !== 'completed'}
              />
              {formData.status !== 'completed' && (
                <p className="text-xs text-gray-500 mt-1">Total runs can only be set for completed matches</p>
              )}
            </div>

            {/* Fetched Data Info */}
            {match?.fetchedData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-2">✅ Data fetched from CricAPI:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="font-medium">Match:</span> {match.fetchedData.name}</div>
                    <div><span className="font-medium">Type:</span> {match.fetchedData.type}</div>
                    <div><span className="font-medium">Status:</span> {match.fetchedData.status}</div>
                    <div><span className="font-medium">Total Runs:</span> {match.fetchedData.totalRuns}</div>
                    <div><span className="font-medium">Winner:</span> {match.fetchedData.winner || 'Not set'}</div>
                    <div><span className="font-medium">Innings:</span> {match.fetchedData.innings}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Fetch POM: try CricAPI / saved URL, or paste ESPN/Cricbuzz URL */}
            {tournamentId && match?.match_id && onFetchPOM && formData.status === 'completed' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Player of the Match
                </label>
                <p className="text-xs text-gray-500">
                  Click &quot;Fetch POM&quot; to try CricAPI and any saved scorecard URL (works for already-processed matches). Or paste a Cricbuzz/ESPN scorecard URL and click Fetch.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={async () => {
                      setFetchingPOM(true);
                      setPomError(null);
                      try {
                        const result = await onFetchPOM('');
                        if (result?.man_of_match) {
                          setFormData((prev) => ({ ...prev, man_of_match_player_name: result.man_of_match! }));
                          setPomUrl('');
                        } else {
                          setPomError(result?.message || 'No POM from CricAPI and no saved scorecard URL.');
                        }
                      } catch (e: any) {
                        setPomError(e?.message || 'Failed to fetch POM.');
                      } finally {
                        setFetchingPOM(false);
                      }
                    }}
                    disabled={fetchingPOM}
                    className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                  >
                    {fetchingPOM ? 'Fetching...' : 'Fetch POM'}
                  </button>
                  <input
                    type="url"
                    value={pomUrl}
                    onChange={(e) => { setPomUrl(e.target.value); setPomError(null); }}
                    placeholder="Or paste Cricbuzz/ESPN scorecard URL"
                    className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                    disabled={fetchingPOM}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const url = pomUrl.trim();
                      if (!url || (!url.includes('espncricinfo.com') && !url.includes('cricbuzz.com'))) {
                        setPomError('Please enter a valid Cricbuzz or ESPN Cricinfo scorecard URL.');
                        return;
                      }
                      setFetchingPOM(true);
                      setPomError(null);
                      try {
                        const result = await onFetchPOM(url);
                        if (result?.man_of_match) {
                          setFormData((prev) => ({ ...prev, man_of_match_player_name: result.man_of_match! }));
                          setPomUrl('');
                        } else {
                          setPomError('Could not find Player of the Match on that page.');
                        }
                      } catch (e: any) {
                        setPomError(e?.message || 'Failed to fetch POM.');
                      } finally {
                        setFetchingPOM(false);
                      }
                    }}
                    disabled={fetchingPOM || !pomUrl.trim()}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
                  >
                    Fetch from URL
                  </button>
                </div>
                {pomError && <p className="text-xs text-red-600">{pomError}</p>}
              </div>
            )}

            {/* Man of the Match */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Man of the Match
              </label>
              {(() => {
                console.log('🔍 Modal render - match.players:', match?.players);
                console.log('🔍 Modal render - players length:', match?.players?.length);
                console.log('🔍 Modal render - players array:', match?.players);
                
                if (match?.players && match.players.length > 0) {
                  console.log('🔍 Modal render - showing dropdown with players');
                  return (
                    <select
                      value={formData.man_of_match_player_name}
                      onChange={(e) => setFormData({ ...formData, man_of_match_player_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={formData.status !== 'completed'}
                    >
                      <option value="">Select Player</option>
                      {match.players.map((player: any) => (
                        <option key={player.id} value={player.name}>
                          {player.name} ({player.team}) - {player.role}
                        </option>
                      ))}
                    </select>
                  );
                } else {
                  console.log('🔍 Modal render - showing text field (no players)');
                  return (
                    <input
                      type="text"
                      value={formData.man_of_match_player_name}
                      onChange={(e) => setFormData({ ...formData, man_of_match_player_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter player name"
                      disabled={formData.status !== 'completed'}
                    />
                  );
                }
              })()}
              {formData.status !== 'completed' && (
                <p className="text-xs text-gray-500 mt-1">Man of the Match can only be set for completed matches</p>
              )}
              {match?.players && match.players.length === 0 && (
                <p className="text-xs text-orange-500 mt-1">⚠️ No squad data available. Please fetch squad data first.</p>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">💡 How this works:</p>
                <ul className="text-xs space-y-1">
                  <li>• Setting results here updates all Pick'em leagues automatically</li>
                  <li>• User points will be recalculated based on their predictions</li>
                  <li>• Only completed matches can have winner, runs, and MoM set</li>
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updating}
                className={`flex-1 px-4 py-2 rounded-md text-white font-medium transition-colors duration-200 ${
                  updating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {updating ? 'Updating...' : 'Save Result'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MatchResultModal; 